/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Grid from "@mui/material/Grid";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, MenuItem } from "@mui/material";

import {
  usePostBiometricDevice,
  usePatchBiometricDevice
} from "../../../../../hooks/query/useBiometricDevices";
import { RootState } from "../../../../../store";
import Panel from "../../../../../components/Panel";
import { tl } from "../../../../../components/translate";
import { IBiometricDevice } from "../../../../../interfaces/BiometricDeviceInterface";

interface BiometricDeviceCreateEditProps {
  onClose: () => void;
  device?: Partial<IBiometricDevice> | null;
  onSuccess: (data?: IBiometricDevice) => void;
}

// eslint-disable-next-line import/prefer-default-export
export function BiometricDeviceCreateEdit({
  onClose,
  device,
  onSuccess
}: BiometricDeviceCreateEditProps): React.ReactNode {
  const isEdit = !!device?.id;
  const rcId = useSelector((state: RootState) => state.userContext.resourceCentre?.id);

  const { mutate: postDeviceMutation } = usePostBiometricDevice();
  const { mutate: patchDeviceMutation } = usePatchBiometricDevice();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<IBiometricDevice>({
    defaultValues: {
      name: device?.name || "",
      ip: device?.ip || "",
      serialNumber: device?.serialNumber || "",
      port: device?.port || 4370,
      model: device?.model || "",
      manufacturer: device?.manufacturer || "",
      location: device?.location || "",
      deviceType: device?.deviceType || "",
      status: device?.status || "active",
      description: device?.description || "",
      resourceCentreId: rcId || 0
    }
  });

  const onSubmit = async (data: IBiometricDevice) => {
    if (!rcId) return;
    const payload: Partial<IBiometricDevice> = {
      ...data,
      resourceCentreId: rcId
    };

    if (isEdit && device?.id) {
      patchDeviceMutation(
        { id: device.id, data: payload },
        {
          onSuccess: (response) => {
            if (response.success) {
              onSuccess(response.data);
              onClose();
            }
          }
        }
      );
    } else {
      postDeviceMutation(payload, {
        onSuccess: (response) => {
          if (response.success) {
            onSuccess(response.data);
            onClose();
          }
        }
      });
    }
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: "flex-end",
        gap: 2
      }}
    >
      <Button onClick={onClose}>{tl("cancel")}</Button>
      <Button
        type="submit"
        variant="contained"
        form="biometric-device-form"
        disabled={isSubmitting}
      >
        {isEdit ? tl("update") : tl("create")}
      </Button>
    </Box>
  );

  return (
    <Panel title={isEdit ? "Edit Device" : "Add Device"} onClose={onClose} footer={footer}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <form id="biometric-device-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Device Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="serialNumber"
                control={control}
                rules={{ required: "Serial Number is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Serial Number"
                    error={!!errors.serialNumber}
                    helperText={errors.serialNumber?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="ip"
                control={control}
                rules={{ required: "IP Address is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="IP Address"
                    error={!!errors.ip}
                    helperText={errors.ip?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="port"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Port"
                    type="number"
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="model"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Model"
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="manufacturer"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Manufacturer"
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Location"
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="deviceType"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Device Type"
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    label="Status"
                    slotProps={{ inputLabel: { shrink: true } }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    label="Description"
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </Panel>
  );
}
